<template>
  <v-card outlined>
    <v-card-title class="text-subtitle-1">
      Aktivitäten
      
       <v-chip v-if="customer.callback_date && customer.callback_date !== '0000-00-00' && !customer.callback_date.startsWith('0000-00-00')" 
              color="orange" text-color="white" small class="ml-2">
        <v-icon left small>mdi-clock-alert</v-icon>
        Aktueller Rückruf: {{ formatCallbackDate(customer.callback_date) }}
        
        <v-btn 
          icon 
          small 
          @click="openResetCallbackDialog" 
          class="ml-1"
          color="white"
        >
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-chip>
      
      <v-spacer></v-spacer>
      <v-btn 
        small 
        color="primary" 
        @click="$emit('openConversation', customer)"
        class="mr-2"
      >
        <v-icon left small>mdi-plus</v-icon>
        Neue Aktivität
      </v-btn>
      <v-btn 
        small 
        color="primary" 
        @click="$emit('openEmail', customer)"
      >
        <v-icon left small>mdi-email</v-icon>
        Send Email
      </v-btn>
    </v-card-title>
    <v-card-text>
      <!-- Conversation Filter -->
      <v-row class="mb-4">
        <v-col cols="12" md="6">
          <v-select
            v-model="conversationFilter"
            :items="conversationFilterOptions"
            label="Filter nach Kontaktperson"
            clearable
            dense
            outlined
          ></v-select>
        </v-col>
      </v-row>

      <div style="max-height: 600px; overflow-y: auto;">
        <v-timeline dense>
          <v-timeline-item
            v-for="conv in filteredConversations"
            :key="conv.id"
            small
          >
            <template v-slot:icon>
              <v-avatar :color="getConversationColor(conv.conversation_type)">
                <v-icon dark small>{{ getConversationIcon(conv.conversation_type) }}</v-icon>
              </v-avatar>
            </template>
            
            <v-card class="elevation-2">
              <v-card-title class="text-subtitle-2 py-2">
                {{ getConversationTypeLabel(conv.conversation_type) }}
                <v-spacer></v-spacer>
                <span class="text-caption">{{ formatDateTime(conv.created_at) }}</span>
              </v-card-title>
              <v-card-text>
                <!-- Contact Person Assignment -->
                <div v-if="conv.contact_person_id" class="mb-2">
                  <v-chip small color="primary">
                    <v-icon left small>mdi-account</v-icon>
                    {{ getContactPersonName(conv.contact_person_id) }}
                  </v-chip>
                </div>
                
                <div v-if="editingConversationId === conv.id" class="mb-2">
                  <v-textarea
                    v-model="editingConversationNote"
                    outlined
                    dense
                    auto-grow
                    rows="3"
                    autofocus
                    @keydown.enter.exact.prevent="saveEditedNote(conv)"
                    @keydown.esc="cancelEditNote"
                    @blur="saveEditedNote(conv)"
                    
                  ></v-textarea>
                </div>
                <div v-else class="conversation-note" @click="startEditNote(conv)" style="white-space: pre-wrap;">
                {{ formatNoteForDisplay(conv.note) || 'Click to add notes' }}
                </div>

                <div v-if="conv.file_name" class="mt-2">
                  <v-chip 
                    small 
                    :color="getFileChipColor(conv.file_name)"
                    @click="openFile(conv)"
                    class="file-chip"
                    :loading="openingFile === conv.id"
                  >
                    <v-icon left small>{{ getFileIcon(conv.file_name) }}</v-icon>
                    {{ conv.file_name }}
                    <v-icon right small>mdi-open-in-new</v-icon>
                  </v-chip>
                
          
                </div>        
              </v-card-text>
              <v-card-actions>
                <div class="d-flex align-center" style="width: 100%;">
                  <div class="text-caption">
                    by: {{ conv.created_by || 'Unknown' }}
                  </div>
                </div>
                <v-spacer></v-spacer>
                <v-btn
                  icon
                  color="red"
                  @click="confirmDeleteConversation(conv)"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-timeline-item>
        </v-timeline>

        <div v-if="filteredConversations.length === 0" class="text-center text--disabled py-5">
          Keine Aktivitäten verfügbar
        </div>
      </div>

      
      <v-dialog
        v-model="deleteDialog"
        max-width="400"
      >
        <v-card>
          <v-card-title class="text-h6">
            Aktivität löschen
          </v-card-title>
          <v-card-text>
            Möchten Sie diese Aktivität wirklich löschen?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="grey"
              text
              @click="deleteDialog = false"
            >
              Abbrechen
            </v-btn>
            <v-btn
              color="error"
              text
              @click="deleteConversation"
              :loading="deleting"
            >
              Löschen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Reset Callback Dialog -->
      <v-dialog
        v-model="resetCallbackDialog"
        max-width="500"
        persistent
      >
        <v-card>
          <v-card-title class="text-h6">
            Rückruf löschen
          </v-card-title>
          <v-card-text>
            <p class="mb-4">Bitte erklären Sie, warum Sie den Rückruf löschen möchten:</p>
            
            <v-radio-group v-model="resetReason" class="mt-0">
              <v-radio value="phone_call" color="primary">
                <template v-slot:label>
                  <div>
                    <strong>Telefonat geführt</strong>
                    <div class="text-caption">Kunde wurde kontaktiert und das Gespräch wurde geführt</div>
                  </div>
                </template>
              </v-radio>
              
              <v-radio value="no_interest" color="primary">
                <template v-slot:label>
                  <div>
                    <strong>Kein Interesse mehr</strong>
                    <div class="text-caption">Kunde hat kein Interesse mehr gezeigt</div>
                  </div>
                </template>
              </v-radio>
              
              <v-radio value="other" color="primary">
                <template v-slot:label>
                  <div>
                    <strong>Anderer Grund</strong>
                    <div class="text-caption">Bitte im Textfeld unten erklären</div>
                  </div>
                </template>
              </v-radio>
            </v-radio-group>

            <v-textarea
              v-if="resetReason === 'other'"
              v-model="customResetReason"
              label="Bitte erklären Sie den Grund"
              rows="3"
              outlined
              class="mt-2"
              :rules="[v => !!v || 'Bitte geben Sie einen Grund an']"
            ></v-textarea>

            <v-textarea
              v-else
              v-model="resetNote"
              label="Zusätzliche Notizen (optional)"
              rows="2"
              outlined
              class="mt-2"
              placeholder="Weitere Details zum Gespräch oder Grund..."
            ></v-textarea>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="grey"
              text
              @click="cancelResetCallback"
              :disabled="resetting"
            >
              Abbrechen
            </v-btn>
            <v-btn
              color="warning"
              text
              @click="confirmResetCallback"
              :loading="resetting"
              :disabled="!canResetCallback"
            >
              Rückruf löschen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar for file errors -->
      <v-snackbar
        v-model="fileErrorSnackbar"
        color="error"
        :timeout="5000"
      >
        {{ fileErrorMessage }}
        <template v-slot:action="{ attrs }">
          <v-btn
            text
            v-bind="attrs"
            @click="fileErrorSnackbar = false"
          >
            Schließen
          </v-btn>
        </template>
      </v-snackbar>

    </v-card-text>
  </v-card>
</template>

<script>
import { storage } from '@/firebase'

export default {
  name: 'Conversations',
  props: {
    customer: Object,
    conversations: Array,
    contactPersons: Array
  },
  data() {
    return {
      conversationFilter: null,
      editingConversationId: null,
      editingConversationNote: '',
      deleteDialog: false,
      conversationToDelete: null,
      deleting: false,
      resetCallbackDialog: false,
      resetting: false,
      resetReason: null,
      customResetReason: '',
      resetNote: '',
      fileErrorSnackbar: false,
      fileErrorMessage: '',
      openingFile: null,
      
    }
  },
  computed: {
    filteredConversations() {
      let conversations = this.conversationFilter 
        ? this.conversations.filter(conv => conv.contact_person_id === this.conversationFilter)
        : [...this.conversations]
      
      // Sort by created_at date, most recent first
      return conversations.sort((a, b) => {
        const dateA = new Date(a.created_at || 0)
        const dateB = new Date(b.created_at || 0)
        return dateB - dateA 
      })
    },
    
    conversationFilterOptions() {
      const options = [{ text: 'Alle Aktivitäten', value: null }]
      
      this.contactPersons.forEach(contact => {
        options.push({
          text: contact.name,
          value: contact.id
        })
      })
      
      return options
    },

    canResetCallback() {
      if (!this.resetReason) return false
      if (this.resetReason === 'other' && !this.customResetReason) return false
      return true
    }
  },
  methods: {
    
    async openFile(conversation) {
      if (!conversation.file_path) {
        this.showFileError('Datei-Pfad nicht verfügbar')
        return
      }

      this.openingFile = conversation.id

      try {
        console.log('Opening file with path:', conversation.file_path)
        
        // Generate download URL from file_path using Firebase Storage
        const fileRef = storage.ref().child(conversation.file_path)
        
        // Get the download URL
        const downloadURL = await fileRef.getDownloadURL()
        console.log('Generated download URL:', downloadURL)
        
        // Open the file in a new tab
        window.open(downloadURL, '_blank', 'noopener,noreferrer')
        
      } catch (error) {
        console.error('Error opening file:', error)
        console.error('Error details:', {
          code: error.code,
          message: error.message
        })
        
        let errorMessage = 'Fehler beim Öffnen der Datei'
        switch (error.code) {
          case 'storage/object-not-found':
            errorMessage = 'Datei wurde nicht in Firebase Storage gefunden'
            break
          case 'storage/unauthorized':
            errorMessage = 'Keine Berechtigung zum Zugriff auf die Datei'
            break
          case 'storage/canceled':
            errorMessage = 'Datei-Zugriff wurde abgebrochen'
            break
          default:
            errorMessage = `Fehler: ${error.message}`
        }
        this.showFileError(errorMessage)
      } finally {
        this.openingFile = null
      }
    },

    // Helper method to show file errors
    showFileError(message) {
      this.fileErrorMessage = message
      this.fileErrorSnackbar = true
    },

    // Helper method to get appropriate icon based on file name
    getFileIcon(fileName) {
      if (!fileName) return 'mdi-file'
      
      const lowerName = fileName.toLowerCase()
      
      if (lowerName.endsWith('.pdf')) return 'mdi-file-pdf-box'
      if (lowerName.endsWith('.doc') || lowerName.endsWith('.docx')) return 'mdi-file-word'
      if (lowerName.endsWith('.xls') || lowerName.endsWith('.xlsx')) return 'mdi-file-excel'
      if (lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg') || lowerName.endsWith('.png') || lowerName.endsWith('.gif')) return 'mdi-file-image'
      
      return 'mdi-file'
    },

    getFileChipColor(fileName) {
      if (!fileName) return 'grey'
      
      const lowerName = fileName.toLowerCase()
      
      if (lowerName.endsWith('.pdf')) return 'red lighten-1'
      if (lowerName.endsWith('.doc') || lowerName.endsWith('.docx')) return 'blue lighten-1'
      if (lowerName.endsWith('.xls') || lowerName.endsWith('.xlsx')) return 'green lighten-1'
      if (lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg') || lowerName.endsWith('.png') || lowerName.endsWith('.gif')) return 'purple lighten-1'
      
      return 'grey'
    },

    formatNoteForDisplay(note) {
      if (!note) return ''
      return note.replace(/\[NEWLINE\]/g, '\n').replace(/\nn/g, '\n')
    },

    formatCallbackDateForNote(callbackDate) {
      if (!callbackDate) return ''
      try {
        const dateObj = new Date(callbackDate)
        const day = String(dateObj.getDate()).padStart(2, '0')
        const month = String(dateObj.getMonth() + 1).padStart(2, '0')
        const year = dateObj.getFullYear()
        const hours = String(dateObj.getHours()).padStart(2, '0')
        const minutes = String(dateObj.getMinutes()).padStart(2, '0')
        return `${day}.${month}.${year} ${hours}:${minutes}`
      } catch (error) {
        return callbackDate
      }
    },

    openResetCallbackDialog() {
      this.resetCallbackDialog = true
      this.resetReason = null
      this.customResetReason = ''
      this.resetNote = ''
    },

    cancelResetCallback() {
      this.resetCallbackDialog = false
      this.resetReason = null
      this.customResetReason = ''
      this.resetNote = ''
    },

    async confirmResetCallback() {
      if (!this.canResetCallback) return

      this.resetting = true

      try {
      
        let note = ''
        
        switch (this.resetReason) {
          case 'phone_call':
            note = `Rückruf gelöscht: Telefonat mit Kunde wurde geführt.${this.resetNote ? '\n' + this.resetNote : ''}`
            break
          case 'no_interest':
            note = `Rückruf gelöscht: Kunde hat kein Interesse mehr gezeigt.${this.resetNote ? '\n' + this.resetNote : ''}`
            break
          case 'other':
            note = `Rückruf gelöscht: ${this.customResetReason}${this.resetNote ? '\n' + this.resetNote : ''}`
            break
        }

        const customerUpdates = {
          callback_date: null,
          callback_time: null,
          isInterest: 'Unknown',
          lastConversation: new Date().toISOString()
        }

        const conversationData = {
          customer_id: this.customer.id,
          conversation_type: 'note',
          note: note,
          user_id: localStorage.getItem('user_id'),
          created_by: localStorage.getItem('username'),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          callback_date: null
        }

        this.$emit('save', {
          conversationData,
          customerUpdates,
          hasCallback: false,
          isReset: true,
        })

        console.log('ConversationsList: Save event emitted successfully')

        this.resetCallbackDialog = false

      } catch (error) {
        console.error('ConversationsList: Error in confirmResetCallback:', error)
      } finally {
        this.resetting = false
      }
    },

    isCallbackConversation(conversation) {
      if (this.customer.callback_date && conversation.created_at) {
        const callbackDate = new Date(this.customer.callback_date)
        const conversationDate = new Date(conversation.created_at)
        const timeDiff = Math.abs(callbackDate - conversationDate)
        return timeDiff < 5 * 60 * 1000 
      }
      
      return false
    },


    confirmDeleteConversation(conversation) {
      this.conversationToDelete = conversation
      this.deleteDialog = true
    },

    async deleteConversation() {
      if (!this.conversationToDelete) return
      
      this.deleting = true
      
      try {
        this.$emit('deleteConversation', this.conversationToDelete)
        this.deleteDialog = false
        this.conversationToDelete = null
      } catch (error) {
        console.error('Error deleting conversation:', error)
      } finally {
        this.deleting = false
      }
    },

    getContactPersonName(contactPersonId) {
      const contact = this.contactPersons.find(cp => cp.id === contactPersonId)
      return contact ? contact.name : 'Unknown'
    },
    
    getConversationColor(type) {
      const colors = {
        phone: 'blue',
        email: 'green',
        meeting: 'purple',
        offer: 'orange',
        note: 'grey'
      }
      return colors[type] || 'grey'
    },

    getConversationIcon(type) {
      const icons = {
        phone: 'mdi-phone',
        email: 'mdi-email',
        meeting: 'mdi-account-group',
        offer: 'mdi-file-document',
        note: 'mdi-note-text'
      }
      return icons[type] || 'mdi-message'
    },

    getConversationTypeLabel(type) {
      const labels = {
        phone: 'Anruf',
        email: 'Email',
        meeting: 'Meeting',
        offer: 'Angebot',
        note: 'Notiz'
      }
      return labels[type] || 'Conversation'
    },

    formatDateTime(date) {
      if (!date) return '-'
      
      try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return '-';
        
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        const hours = String(dateObj.getHours()).padStart(2, '0');
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        
        return `${day}.${month}.${year} ${hours}:${minutes}`;
      } catch (error) {
        console.error('Error formatting date time:', date, error);
        return '-';
      }
    },

    formatCallbackDate(callbackDate) {
      if (!callbackDate) return '-'
      
      try {
        const dateObj = new Date(callbackDate);
        if (isNaN(dateObj.getTime())) return '-';
        
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        const hours = String(dateObj.getHours()).padStart(2, '0');
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        
        return `${day}.${month}.${year} ${hours}:${minutes}`;
      } catch (error) {
        console.error('Error formatting callback date:', callbackDate, error);
        return '-';
      }
    },

    startEditNote(conversation) {
      this.editingConversationId = conversation.id
      this.editingConversationNote = this.formatNoteForDisplay(conversation.note) || ''
    },

    cancelEditNote() {
      this.editingConversationId = null
      this.editingConversationNote = ''
    },

    async saveEditedNote(conversation) {
      if (this.editingConversationNote === conversation.note) {
        this.cancelEditNote()
        return
      }
      const encodednote = this.editingConversationNote.replace(/\n/g, '[NEWLINE]')
      this.$emit('editNote', {
        conversation,
        note: encodednote
      })
      
      this.cancelEditNote()
    }
  }
}
</script>

<style scoped>
.conversation-note {
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
}

.conversation-note:hover {
  background-color: #f5f5f5;
}

.file-chip {
  cursor: pointer;
  transition: all 0.2s ease;
}

.file-chip:hover {
  opacity: 0.8;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}
</style>